import { SsoEmailDomain } from '@choco-admin/types';

import { useSsoPageEmailDomainsQuery } from '../../../generated';
import { mapToSsoEmailDomain } from '../utils';

export interface UseSsoEmailDomainsInput {
  connectionId: string;
}

export interface UseSsoEmailDomainsResult {
  /** Only the domains someone has flagged. Cognito's routed list is elsewhere. */
  emailDomains: SsoEmailDomain[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * The domains this connection holds a stored flag for.
 *
 * Not the list to build the toggles from — it holds only flagged domains, so a
 * routed domain nobody has flagged is missing from it. Its use is the opposite
 * case: a domain that keeps its stored flag after Cognito stopped routing it,
 * which appears here and not in the connection's own list.
 */
export const useSsoEmailDomains = ({
  connectionId,
}: UseSsoEmailDomainsInput): UseSsoEmailDomainsResult => {
  const { data, loading, error, refetch } = useSsoPageEmailDomainsQuery({
    variables: { input: { connectionId } },
    fetchPolicy: 'cache-and-network',
    skip: !connectionId,
  });

  const result = data?.ssoEmailDomains;

  const authorizationError =
    result?.__typename === 'AuthorizationFailure'
      ? new Error(result.message ?? 'Not authorized to view email domains.')
      : null;

  return {
    emailDomains:
      result?.__typename === 'SsoEmailDomainList'
        ? result.emailDomains.map(mapToSsoEmailDomain)
        : [],
    isLoading: loading,
    error: error ?? authorizationError,
    refetch: () => {
      refetch();
    },
  };
};
